import React from 'react';
import { useRef, useState, useEffect } from 'react';
import { SmallInputField } from '../../../components/shareable/input';
import { SmallDropdown, ContactDropdown } from '../../../components/shareable/dropdownMenu';
import CustomModal from '../newQuote/component/customModal';
import AddContactModal from '../newQuote/component/AddContactModal';
import TableComponent from '../newQuote/component/tableComponent';
import SpacerModal from '../newQuote/component/SpacerModal';
import SawConveyorDetails from '../newQuote/component/SawConveyorDetails';
import ControlsRow from '../newQuote/component/ControlsRow';
import BoxesSection from '../newQuote/component/BoxesSection';
import QuoteDetailsModal from '../newQuote/component/QuoteDetailsModal';
import { Plus } from 'lucide-react';
import useQuote from '../newQuote/useQuote';
import { LENGTH_LIMIT_MAX_FT } from '../../../utils/defaultValues';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import { useParams, useNavigate } from 'react-router-dom';

const UpdateQuote = () => {
  const { id: quoteId } = useParams();
  const navigate = useNavigate();
  const {
    topRef,
    // values
    quoteNumber, formFields, customers, selectedCustomerName, dropdownOptions, selectedConveyorStyle, selectedRollerLength, sawOptions,
    infeedLength, outfeedLength, items, isRotatedArrow, orientation, isFlipped, leftBoxItems, rightBoxItems, isRotatedBox, checkedItems,
    leftBoxConveyorStyle, rightBoxConveyorStyle, isReversed, flippedBoxes, motorSelections, taSelections, filenameFlipStates, stkSelections,
    globalImageCache, leftBoxCacheClear, rightBoxCacheClear, motorVariantImageUrls, taImageUrls, stkImageUrls, handleMotorSelect, motorVariantByBox,
    sawImage, selectedSaw, isAddingNew, customModalOpen, isQuoteCreated, showLengthLimitModal, lengthLimitMessage, showQuoteDetailsModal,
    showSpacerModal, sampleData, buttonsEnabled, selectedCustomer, quoteDetails, quoteBrochure, setSampleData,setSelectedSaw, stkVariantByPortion, taVariantByPortion, selectedContact,
    // setters
    setInfeedLength, setLengthLimitMessage, setShowLengthLimitModal, setOutfeedLength, setIsFlipped, setIsRotatedBox, setGlobalImageCache,
    setActiveBox, setShowQuoteDetailsModal, setCustomModalOpen, setQuoteDetails, setQuoteBrochure, setShowSpacerModal, setSelectedContact,
    // handlers
    getTodayDate, handleInputChange, handleCustomerSelection, handleConveyorStyleSelection, getCurrentRollerLengths, handleRollerLengthSelect,
    handleSawSelection, handleRemoveSaw, handleAddNewSaw, handleArrowClick, handleVerticalFlip, handleCheckboxChange, handleBoxItemsChange, handleCheckedItemsChange, handleClearBox,
    generateConveyorFilename, getImageForSelection, getImageKey, handleAddNew, handleTableSave, handleClearTable, handleCloseModal,
    handleSubmitData, handleUpdateQuoteButton, handleSpacerConfirm, calculateTotalLength, handleCancelAddNew, handle10FtModel, handle5FtModel, handleLiftTable, handle10FtForkModel, handleDeletePortion, handleSTKChange, handleTAChange, handleAddContact
  } = useQuote();

  const [showAddContactModal, setShowAddContactModal] = useState(false);

  const contactItems = React.useMemo(() => {
    if (!selectedCustomer) return [];
    const c = selectedCustomer.customerData || selectedCustomer;
    const items = [];
    if (c.primary_contact) {
      items.push({
        key: 'primary',
        name: c.primary_contact,
        subtitle: c.title || c.email || c.phone || '',
        contact: {
          source: 'primary', location_id: null, name: c.primary_contact || '', title: c.title || '',
          phone: c.phone || '', email: c.email || '',
          location: [c.address, c.address2, c.city, c.state, c.zip].filter(Boolean).join(', ')
        }
      });
    }
    (c.additional_locations || []).forEach((loc) => {
      const key = loc._id || `${loc.location}-${loc.primary_contact}`;
      items.push({
        key,
        name: loc.primary_contact || '(unnamed)',
        subtitle: loc.location || loc.email || '',
        contact: {
          source: 'additional', location_id: loc._id || null,
          name: loc.primary_contact || '', title: '',
          phone: loc.primary_contact_number || '', email: loc.email || '',
          location: loc.location || ''
        }
      });
    });
    return items;
  }, [selectedCustomer]);

  const selectedContactKey = React.useMemo(() => {
    if (!selectedContact) return null;
    if (selectedContact.source === 'primary') return 'primary';
    if (selectedContact.location_id) return selectedContact.location_id;
    const match = contactItems.find(i => i.contact.name === selectedContact.name && i.contact.email === selectedContact.email);
    return match?.key || null;
  }, [selectedContact, contactItems]);

  const infeedRef = useRef(null);
  const outfeedRef = useRef(null);
  const sawRef = useRef(null);
  const arrowRef =useRef(null);
  const tableRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const sortedCustomers = React.useMemo(() => {
    return [...customers].sort((a, b) => { const nameA = (a.customer || a.customerData?.customer || '').toLowerCase(); const nameB = (b.customer || b.customerData?.customer || '').toLowerCase(); return nameA.localeCompare(nameB); });
  }, [customers]);

  useEffect(() => {
    if (!quoteId) {
      setIsLoadingData(false);
      return;
    }

    // Check if initial data (customers, saws) is loaded
    const hasInitialData = customers.length > 0;
    
    // Check if quote data is loaded - look for indicators that fetchQuoteData has completed:
    const hasQuoteData = quoteNumber && (
      items.length > 0 || 
      leftBoxItems.length > 0 || 
      rightBoxItems.length > 0 || 
      sampleData.length > 0 ||
      (formFields && formFields.quotePrice !== undefined && formFields.quotePrice !== '') ||
      selectedCustomerName !== ''
    );
    
    if (hasInitialData && hasQuoteData) {
      const timer = setTimeout(() => { setIsLoadingData(false); }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsLoadingData(true);
    }
  }, [quoteId, customers, quoteNumber, items, leftBoxItems, rightBoxItems, sampleData, formFields, selectedCustomerName]);

  return (
    <div className="p-2  space-y-2 relative -mt-4">
      <div ref={topRef} className="absolute top-0"></div>

      {/* Quote Info + Customer + Buttons in One Row */}
      <div className="grid grid-cols-10 gap-2 items-stretch">
          {/* Quote Info & Date - 4 columns */}
          <div className="col-span-10 md:col-span-4 flex flex-col">
            <h2 className="text-xs font-medium text-gray-600 mb-1">Quote Info & Date</h2>
            <div className="bg-white p-2 rounded shadow grid grid-cols-1 md:grid-cols-3 gap-2 h-full">
              <SmallInputField label="Quote Number" value={quoteNumber} readOnly width="w-full"/>
              <SmallInputField label="Quote Price ($)" type="number" value={formFields.quotePrice} onChange={(e) => handleInputChange("quotePrice", e.target.value)} placeholder="0.00" step="0.01" width="w-full"/>
              <SmallInputField label="Date" type="date" value={getTodayDate()} readOnly width="w-full"/>
            </div>
          </div>

          {/* Customer & RFQ - 3 columns */}
          <div className="col-span-10 md:col-span-6 flex flex-col">
            <h2 className="text-xs font-medium text-gray-600 mb-1">Customer Details & Discount</h2>
            <div className="bg-white p-1.5 rounded shadow flex flex-wrap items-start gap-2 h-full mt-0.5">
              <SmallDropdown label="Customer" items={sortedCustomers.map((c) => c.customer || c.customerData?.customer || "Unnamed Customer")} selected={selectedCustomerName} onSelect={(value) => handleCustomerSelection(value)} width="w-72" />
              <SmallInputField label="Customer RFQ" value={formFields.customerRFQ} onChange={(e) => handleInputChange("customerRFQ", e.target.value)} placeholder="Enter RFQ" width="w-36"/>
              <SmallInputField label="Standard Discount %" value={formFields.standard_discount || ""} onChange={(e) => handleInputChange("standard_discount", e.target.value)} placeholder="Discount %" width="w-40" readOnly/>
              <ContactDropdown
                label="Primary Contact"
                items={contactItems}
                selectedKey={selectedContactKey}
                onSelect={(item) => setSelectedContact(item.contact)}
                onAddNew={selectedCustomer ? () => setShowAddContactModal(true) : null}
                disabled={!selectedCustomer}
                width="w-44"
              />
            </div>
          </div>
        </div>

        {/* Saw Section */}
        <SawConveyorDetails setSelectedSaw={setSelectedSaw} dropdownOptions={dropdownOptions} selectedConveyorStyle={selectedConveyorStyle} handleConveyorStyleSelection={handleConveyorStyleSelection} getCurrentRollerLengths={getCurrentRollerLengths} selectedRollerLength={selectedRollerLength} handleRollerLengthSelect={handleRollerLengthSelect} sawOptions={sawOptions} handleSawSelection={handleSawSelection} handleRemoveSaw={handleRemoveSaw} handleAddNewSaw={handleAddNewSaw} formFields={formFields} selectedSaw={selectedSaw} quoteBrochure={quoteBrochure} setQuoteBrochure={setQuoteBrochure} />

        {/* Buttons - 5 columns in a row */}
        <div className="col-span-10 md:col-span-5 flex flex-col">
          {/* Status message block */}
          <div className="mb-1">
            {!selectedConveyorStyle && (<p className="text-yellow-600 text-xs">Please select a conveyor style to continue</p>)}
            {selectedConveyorStyle && !selectedRollerLength && (<p className="text-yellow-600 text-xs">Please select a roller length to enable configuration options</p>)}
            {selectedConveyorStyle && selectedRollerLength && (<p className="text-green-600 text-xs">You can now add configuration options</p>)}
          </div>

          {/* Button Grid */}
          <div className="grid grid-cols-5 gap-0.5 w-full bg-white p-1 rounded shadow">
            <button onClick={handle10FtModel} disabled={!buttonsEnabled.tenFt} className={`bg-gradient-to-r from-red-600 to-red-800 text-white text-[15px] font-medium px-1 py-[2px] rounded shadow ${!buttonsEnabled.tenFt ? "opacity-50 cursor-not-allowed" : ""}`}>Add 10FT Model</button>
            <button onClick={handle10FtForkModel} disabled={!buttonsEnabled.tenFtFork} className={`bg-gradient-to-r from-red-600 to-red-800 text-white text-[15px] font-medium px-1 py-[2px] rounded shadow ${!buttonsEnabled.tenFtFork ? "opacity-50 cursor-not-allowed" : ""}`}>Add 10FT Model with Forklift</button>
            <button onClick={handle5FtModel} disabled={!buttonsEnabled.fiveFt} className={`bg-gradient-to-r from-red-600 to-red-800 text-white text-[15px] font-medium px-1 py-[2px] rounded shadow ${!buttonsEnabled.fiveFt ? "opacity-50 cursor-not-allowed" : ""}`}>Add 5FT Model</button>
            <button onClick={handleLiftTable} disabled={!buttonsEnabled.liftTable} className={`bg-gradient-to-r from-red-600 to-red-800 text-white text-[15px] font-medium px-1 py-[2px] rounded shadow ${!buttonsEnabled.liftTable ? "opacity-50 cursor-not-allowed" : ""}`}>Add Power Lift Table</button>
            <button onClick={() => setShowSpacerModal(true)} disabled={!buttonsEnabled.spacer} className={`bg-gradient-to-r from-red-600 to-red-800 text-white text-[15px] font-medium px-1 py-[2px] rounded shadow ${!buttonsEnabled.spacer ? "opacity-50 cursor-not-allowed" : ""}`}>Add Space</button>
          </div>
        </div>

        {/* Infeed/Outfeed Controls & Arrow & Boxes */}
        <div className="w-full bg-white py-1 px-4 rounded-xl shadow-md">

          {/* Controls Row */}
          <ControlsRow arrowRef={arrowRef} handleClearBox={handleClearBox} infeedLength={infeedLength} setInfeedLength={setInfeedLength} setLengthLimitMessage={setLengthLimitMessage} setShowLengthLimitModal={setShowLengthLimitModal} handleArrowClick={handleArrowClick} items={items} isRotatedArrow={isRotatedArrow} orientation={orientation} outfeedLength={outfeedLength} setOutfeedLength={setOutfeedLength} isGeneratingPdf={isGeneratingPdf} setIsGeneratingPdf={setIsGeneratingPdf} />

          {/* Boxes and Saw Image */}
          <BoxesSection isFlipped={isFlipped} sawRef={sawRef} isGeneratingPdf={isGeneratingPdf} setIsFlipped={setIsFlipped} handleVerticalFlip={handleVerticalFlip} leftBoxItems={leftBoxItems} rightBoxItems={rightBoxItems} isRotatedBox={isRotatedBox} setIsRotatedBox={setIsRotatedBox} checkedItems={checkedItems} handleCheckboxChange={handleCheckboxChange} leftBoxConveyorStyle={leftBoxConveyorStyle} rightBoxConveyorStyle={rightBoxConveyorStyle} generateConveyorFilename={generateConveyorFilename} isReversed={isReversed} flippedBoxes={flippedBoxes} handleBoxItemsChange={handleBoxItemsChange} handleCheckedItemsChange={handleCheckedItemsChange} setActiveBox={setActiveBox} motorSelections={motorSelections} taSelections={taSelections} filenameFlipStates={filenameFlipStates} stkSelections={stkSelections} globalImageCache={globalImageCache} setGlobalImageCache={setGlobalImageCache} leftBoxCacheClear={leftBoxCacheClear} rightBoxCacheClear={rightBoxCacheClear} getImageForSelection={getImageForSelection} getImageKey={getImageKey} motorVariantImageUrls={motorVariantImageUrls} taImageUrls={taImageUrls} stkImageUrls={stkImageUrls} handleMotorSelect={handleMotorSelect} motorVariantByBox={motorVariantByBox} sawImage={sawImage} selectedSaw={selectedSaw} handleDeletePortion={handleDeletePortion} sampleData={sampleData} setSampleData={setSampleData} infeedRef={infeedRef} outfeedRef={outfeedRef} handleSTKChange={handleSTKChange} stkVariantByPortion={stkVariantByPortion} handleTAChange={handleTAChange} taVariantByPortion={taVariantByPortion}/>

        </div>

        {/* Quote Details And Table */}
        <div className="w-full max-w-7xl mx-auto px-4 py-8 bg-white shadow-lg rounded-xl">
          <div className="flex justify-between items-center mb-6 border-b pb-2 -mt-2">
            <h1 className="text-xl font-extrabold text-red-600">Quotes Details</h1>
            <div className="flex gap-3">
              <button onClick={handleAddNew} disabled={isAddingNew} className={`px-4 py-2.5 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-opacity-50 active:scale-[0.98] border ${isAddingNew ? "bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed" : "bg-slate-200 text-gray-800 hover:bg-slate-300 hover:shadow-md focus:ring-gray-300 border-gray-300"}`}><Plus className="h-4 w-4" strokeWidth={2.5} /><span>New Item</span></button>
              <button onClick={() => setShowQuoteDetailsModal(true)} className="bg-gradient-to-r from-red-600 to-red-800 text-white font-medium px-4 py-2 rounded-lg shadow hover:scale-105 transition">Additional Quote Details</button>
            </div>
          </div>

          <div className="overflow-x-auto mb-8">
            <TableComponent ref={tableRef} quoteDetails={quoteDetails} setQuoteDetails={setQuoteDetails} quoteBrochure={quoteBrochure} data={sampleData} arrowRef={arrowRef} quoteNumber={quoteNumber} sawDetails={selectedSaw} workBedHeight={formFields.workBedHeight} discount={formFields.standard_discount} customerData={selectedCustomer} selectedContact={selectedContact} onSave={handleTableSave} onClear={handleClearTable} onExit={handleCloseModal} onPublish={handleSubmitData} isAddingNew={isAddingNew} onCancelAddNew={handleCancelAddNew} infeedRef={infeedRef} outfeedRef={outfeedRef} sawRef={sawRef} leftBoxItems={leftBoxItems} rightBoxItems={rightBoxItems} checkedItems={checkedItems} leftBoxConveyorStyle={leftBoxConveyorStyle} rightBoxConveyorStyle={rightBoxConveyorStyle} generateConveyorFilename={generateConveyorFilename} motorSelections={motorSelections} motorVariantByBox={motorVariantByBox} isGeneratingPdf={isGeneratingPdf} setIsGeneratingPdf={setIsGeneratingPdf} />
          </div>

          <CustomModal isOpen={customModalOpen} onCancel={() => setCustomModalOpen(false)} onConfirm={(value) => { setCustomModalOpen(false); }} />
          {/* Update Quote Button */}
          <div className="flex justify-center -mt-2"><button onClick={async () => {
            // Save first so the server can bump the quote version and return the new
            // quote_number. Then generate the PDF against that new number so the
            // Quotes Data page can find the PDF for the freshly-saved record.
            const result = await handleUpdateQuoteButton(async (newQuoteNumber) => {
              if (tableRef.current?.triggerGenerateAndSave) {
                tableRef.current.triggerGenerateAndSave(() => navigate('/quotes'), newQuoteNumber);
              } else {
                navigate('/quotes');
              }
            });
            // If save failed, the onSuccess callback above never ran; nothing else to do.
            if (!result?.success) return;
          }} disabled={isQuoteCreated || isAddingNew || isGeneratingPdf} className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 ${isQuoteCreated || isAddingNew || isGeneratingPdf ? "opacity-50 cursor-not-allowed bg-slate-400 hover:bg-slate-500" : ""}`}>{isQuoteCreated ? "Quote Updated" : "Update Quote"}</button></div>
        </div>

        <CustomModal isOpen={showLengthLimitModal} onCancel={() => setShowLengthLimitModal(false)} onConfirm={() => setShowLengthLimitModal(false)} title="Length Limit Reached" message={lengthLimitMessage} confirmText="OK" showCancel={false} />

        <QuoteDetailsModal isOpen={showQuoteDetailsModal} onClose={() => setShowQuoteDetailsModal(false)} quoteDetails={quoteDetails} setQuoteDetails={setQuoteDetails} />

        <SpacerModal isOpen={showSpacerModal} onClose={() => setShowSpacerModal(false)} onConfirm={handleSpacerConfirm} initialValue={Math.min(LENGTH_LIMIT_MAX_FT - calculateTotalLength(isRotatedBox ? rightBoxItems : leftBoxItems), 10)} maxValue={LENGTH_LIMIT_MAX_FT - calculateTotalLength(isRotatedBox ? rightBoxItems : leftBoxItems)} />

        <AddContactModal
          isOpen={showAddContactModal}
          onClose={() => setShowAddContactModal(false)}
          customerName={selectedCustomerName}
          onConfirm={async (loc) => {
            const res = await handleAddContact(loc);
            if (res?.success) setShowAddContactModal(false);
          }}
        />

        <LoadingModal loading={isLoadingData} isGeneratingPdf={false} />
    </div>
  );
};

export default UpdateQuote;
